/* eslint-disable no-undef */
import Qty from 'js-quantities';
import MovementProfile from './MovementProfile';
import ApplicationData from './ApplicationData';
import Results from './Results';
import Vue from 'vue';
import Motor from './Motor';
import { setUnitProfiles, getUnitProfile } from '../helpers/UnitHelpers';

import d from '../download';
require('../moment');

class Scenario {
    _profiles = [];

    _applicationData;

    _motor;

    name;

    id;

    results;

    timer;

    simulating = false;

    update = true;

    listener;

    times = [];

    static DELAY = 250;

    hasTemperatureData() {
        return !(
            this.graphs !== undefined &&
            (this.graphs.tt === undefined || Object.keys(this.graphs.tt).length <= 0)
        );
    }

    convert(unitSystem = 'si', updateUnitProfile = true) {
        this.update = false;

        if (updateUnitProfile) {
            setUnitProfiles(unitSystem);
        }

        this.unit_profile = getUnitProfile(unitSystem);

        if (this.profiles !== undefined) {
            this.profiles.forEach(profile => {
                profile.convert(unitSystem);
            });
        }

        if (this.applicationData !== undefined) {
            this.applicationData.convert(unitSystem);
        }
        this.update = true;
    }

    set applicationData(newVal) {
        if (newVal instanceof ApplicationData) {
            this._applicationData = newVal;

            this._applicationData.listen(obj => this.changed(obj));
        }
    }

    get applicationData() {
        if (this._applicationData === undefined || this._applicationData === null) {
            this._applicationData = new ApplicationData(this);
            this._applicationData.listen(obj => this.changed(obj));
        }

        return this._applicationData;
    }

    set profiles(newVal) {
        this._profiles = newVal;
    }

    get profiles() {
        if (this._profiles === undefined || this._profiles === null) {
            this._profiles = [];
        }

        return this._profiles;
    }

    addMovementProfile(index, profile) {
        profile.listen(obj => this.changed(obj));
        this.profiles.splice(index, 0, profile);
        this.changed('addMovementProfile');
    }

    pushMovementProfile(profile) {
        if (this.profiles === undefined || this.profiles === null) {
            this.profiles = [];
        }

        profile.listen(obj => this.changed(obj));
        this.profiles.push(profile);
        this.changed('pushMovementProfile');
    }

    deleteMovementProfile(profile) {
        this.profiles = this.profiles.filter(item => {
            return item !== profile;
        });

        this.changed('deleteMovementProfile');
    }

    set motor(newVal) {
        let change = true;
        if (this._motor === undefined || this._motor.isEmpty()) {
            change = false;
        }

        this._motor = newVal;
        if (change) {
            this.changed('motor');
        }
    }

    get motor() {
        if (this._motor === undefined || this._motor === null) {
            return {};
        }

        return this._motor;
    }

    set results(newVal) {
        this._results = newVal;
    }

    get results() {
        if (this._results === undefined || this._results === null) {
            return {};
        }

        return this._results;
    }

    /**
     * Create a deep copy of the object.
     * @returns {*}
     */
    copy() {
        let profiles = [];
        this.profiles.forEach(profile => {
            profiles.push(profile.copy());
        });

        let copy = new Scenario();
        copy.id = this.id;
        copy.name = this.name;
        copy.motor = this.motor.copy();
        copy.applicationData = this.applicationData.copy();
        copy.profiles = profiles;

        return copy;
    }

    toJson() {
        let profiles = [];
        this.profiles.forEach(profile => {
            profiles.push(profile.toJson(this.isTorque()));
        });

        let appJson = this.applicationData.toJson(this.isTorque());

        let json = {
            application_data: appJson,
            profiles: profiles,
            dc: appJson.dc,
        };

        if (this.id) {
            json.id = this.id;
        }

        if (this.name) {
            json.name = this.name;
        }

        if (this._motor) {
            json.motor = this.motor.toJson();
        }

        return json;
    }

    listen(callback) {
        this.listener = callback;
    }

    changed(obj) {
        if (!this.simulating && this.update) {
            //Handle change listeners

            if (this.timer !== undefined) {
                clearTimeout(this.timer);
            }

            this.timer = setTimeout(() => {
                if (
                    !this.simulating &&
                    this.update &&
                    this.listener !== undefined &&
                    this.listener !== null
                ) {
                    this.listener(obj);
                }

                clearTimeout(this.timer);
            }, Scenario.DELAY);
        }
    }

    static fromJson(json, units = 'si') {
        let scenario = new Scenario();

        //Converting json to object based on SI units.
        setUnitProfiles('si');

        scenario.update = false;
        if (json !== undefined) {
            scenario.name = json.name;
            scenario.id = json.id;
            scenario.motor = Motor.fromJson(json.motor);
            scenario.applicationData = ApplicationData.fromJson(
                json.application_data,
                json.dc ? 'dc' : 'ac',
                scenario.isTorque(),
                scenario,
            );
            scenario.results = Results.fromJson(json.results);

            let profilesJson = json.profiles;
            if (profilesJson !== undefined) {
                for (let i = 0; i < profilesJson.length; i++) {
                    let p = MovementProfile.fromJson(profilesJson[i], scenario.isTorque());
                    scenario.pushMovementProfile(p);
                }
            }
        }
        scenario.update = true;

        return scenario;
    }

    save() {
        let cpy = this.copy();
        cpy.update = false;
        cpy.convert('si');

        let profiles = [];
        if (cpy.profiles !== undefined) {
            for (let i = 0; i < cpy.profiles.length; i++) {
                let p = cpy.profiles[i].toJson(this.isTorque());
                p.order = i;
                profiles.push(p);
            }
        }

        let body = {
            application_data: cpy.applicationData.toJson(this.isTorque()),
            motor: cpy.motor.name,
            profiles: profiles,
            save: true,
            id: cpy.id,
            name: cpy.name,
        };

        let req = Vue.api.put('/simulate/');
        req.json = false;

        return new Promise((resolve, reject) => {
            req.send(JSON.stringify(body))
                .then(types => {
                    this.simulating = false;
                    resolve(this);
                })
                .catch(err => {
                    console.warn('[ save ] ERR', err);

                    this.simulating = false;
                    reject(this);
                });
        });
    }

    saveAs(name) {
        let cpy = this.copy();
        cpy.update = false;
        cpy.convert('si');

        let profiles = [];
        if (cpy.profiles !== undefined) {
            for (let i = 0; i < cpy.profiles.length; i++) {
                let p = cpy.profiles[i].toJson(this.isTorque());
                p.order = i;
                profiles.push(p);
            }
        }

        let body = {
            application_data: cpy.applicationData.toJson(this.isTorque()),
            motor: cpy.motor.name,
            profiles: profiles,
            save: true,
            name: name,
        };

        cpy.name = name;

        let req = Vue.api.post('/simulate/');

        return new Promise((resolve, reject) => {
            req.send(JSON.stringify(body))
                .then(types => {
                    cpy.id = types.id;

                    this.simulating = false;
                    resolve(cpy);
                })
                .catch(err => {
                    console.warn('[ save ] ERR', err);

                    this.simulating = false;
                    reject(this);
                });
        });
    }

    simulate(units = 'si') {
        let cpy = this.copy();
        cpy.update = false;
        cpy.convert('si', false);

        let profiles = [];
        if (cpy.profiles !== undefined) {
            for (let i = 0; i < cpy.profiles.length; i++) {
                let p = cpy.profiles[i].toJson(this.isTorque());
                p.order = i;
                profiles.push(p);
            }
        }

        let appJson = cpy.applicationData.toJson(cpy.isTorque());

        let body = {
            application_data: appJson,
            motor: cpy.motor.name,
            profiles: profiles,
            units: units,
            dc: appJson.dc,
        };

        let req = Vue.api.post('/simulate/');

        return new Promise((resolve, reject) => {
            if (!this.simulating) {
                this.simulating = true;

                req.send(JSON.stringify(body))
                    .then(types => {
                        let results = types.results;

                        this.results = Results.fromJson(results.stats);
                        this.graphs = results.graphs;
                        this.times = results.profile_times;

                        this.simulating = false;
                        resolve(this);
                    })
                    .catch(err => {
                        console.warn('[ simulation ] ERR', err);

                        this.simulating = false;
                        reject(err);
                    });
            } else {
                this.simulating = false;
                resolve(this);
            }
        });
    }

    pdf(units = 'si') {
        let cpy = this.copy();
        cpy.update = false;
        cpy.convert('si', false);

        let profiles = [];
        if (cpy.profiles !== undefined) {
            for (let i = 0; i < cpy.profiles.length; i++) {
                let p = cpy.profiles[i].toJson(cpy.isTorque());
                p.order = i;
                profiles.push(p);
            }
        }

        let appJson = cpy.applicationData.toJson(cpy.isTorque());
        let body = {
            application_data: appJson,
            motor: cpy.motor.name,
            profiles: profiles,
            units: units,
            output: 'pdf',
            dc: appJson.dc,
        };

        if (cpy.name) {
            body.name = cpy.name;
        }

        let req = Vue.api.post('/simulate/');
        req.json = false;

        return new Promise((resolve, reject) => {
            if (!this.simulating) {
                this.simulating = true;

                req.send(JSON.stringify(body))
                    .then(resp => {
                        this.simulating = false;
                        resp.blob().then(blob => {
                            window.open(window.URL.createObjectURL(blob));
                            resolve(this);
                        });
                    })
                    .catch(err => {
                        this.simulating = false;
                        reject(this);
                    });
            } else {
                this.simulating = false;
                resolve(this);
            }
        });
    }

    generateFileName() {
        let name = '';

        if (this.name) {
            name += this.name.replace(/\W/g, '_');
        } else {
            name += 'scenario';
        }

        name += '_' + window.moment().format('YYYY_MM_DD') + '.pdf';
        return name;
    }

    isTorque() {
        if (this.motor !== undefined && this.motor !== null && this.motor.name !== undefined) {
            return this.motor.name.toLowerCase().startsWith('q');
        }
        return false;
    }

    static quote(id, message = '') {
        let req = Vue.api.post('/share_simulation/');

        let body = {
            simulation: id,
            share_type: 'sales',
            message: message,
        };

        return new Promise((resolve, reject) => {
            req.send(JSON.stringify(body))
                .then(resp => {
                    resolve(this);
                })
                .catch(err => {
                    reject(this);
                });
        });
    }

    static help(id, message = '', contact = '') {
        let req = Vue.api.post('/share_simulation/');

        let body = {
            simulation: id,
            share_type: 'help',
            contact: contact,
            message: message,
        };

        return new Promise((resolve, reject) => {
            req.send(JSON.stringify(body))
                .then(resp => {
                    resolve(this);
                })
                .catch(err => {
                    reject(this);
                });
        });
    }

    static share(id, email, message = '') {
        let req = Vue.api.post('/share_simulation/');

        let body = {
            simulation: id,
            share_type: 'share',
            message: message,
            to_email: email,
        };

        return new Promise((resolve, reject) => {
            req.send(JSON.stringify(body))
                .then(resp => {
                    resolve(this);
                })
                .catch(err => {
                    reject(this);
                });
        });
    }
}

export default Scenario;
